import { _, Features, NullableString, MaskedDate, FeatureArray, ImageBlob, FingerprintsBlob, Enum, FeaturesI18N, DocumentField } from 'controls';
import { uFormat, UTCDate } from 'types';
import { Gender, PersonIdentity, NameChange, ApplicantIdentityDocument, RepresentativeIdentityDocument, FPMailingInfo, NotificationOfRefusalFormFields, CitizenshipCertificate, AdultSpecialConditions, TeenagerSpecialConditions, PresidentEdict, CitizenshipDecision, SoldierPermission, InputMedia, MobileOptions, JobRecord, ApplicantIdentityDocumentType, AmbulanceOptions, StationaryOptions, FingerprintsRequired, ForeignPassportType, PersonalizedFPassportInfo, RequestPriority, RequestGoal, HRManager, DipServiceControlData, InputMode } from './features';
import { Utils } from './utils';

export class PersonAddressV2 {
  @_ country: string = '';
  @_ admDivision: NullableString = new NullableString();
  @_ region: NullableString = new NullableString();
  @_ settlement: string = '';
  @_ street: NullableString = new NullableString();
  @_ building: NullableString = new NullableString();
  @_ housing: NullableString = new NullableString();
  @_ structure: NullableString = new NullableString();
  @_ flat: NullableString = new NullableString();
}

export class PermAddressV2 {
  @_ address: PersonAddressV2 = new PersonAddressV2();
  @_ from: MaskedDate = new MaskedDate();
}
Features.maskedDateOptions(PermAddressV2, x => x.from, true, false, false, null);

export class TempAddressTypeV2 extends Enum {
  static readonly Registered_PREBYVANIE: TempAddressTypeV2 = new TempAddressTypeV2('0');
  static readonly NotRegistered_FAKTICHESKOE_PROZHIVANIE: TempAddressTypeV2 = new TempAddressTypeV2('1');
}

export class TempAddressV2 {
  @_ type: TempAddressTypeV2 = TempAddressTypeV2.NotRegistered_FAKTICHESKOE_PROZHIVANIE;
  @_ address: PersonAddressV2 = new PersonAddressV2();
  @_ from: MaskedDate = new MaskedDate();
  @_ to: MaskedDate = new MaskedDate();
}
Features.maskedDateOptions(TempAddressV2, x => x.from, true, false, false, null);
Features.maskedDateOptions(TempAddressV2, x => x.to, true, false, false, null);

export class ActiveFPassportInfoV2 {
  @_ series: string = '';
  @_ number: string = '';
  @_ issuer: string = '';
  @_ issueDate: MaskedDate = new MaskedDate();
  @_ mustBeCanceled: boolean = false;
  @_ lost: boolean = false;
  @_ next: boolean = false;
}
Features.maskedDateOptions(ActiveFPassportInfoV2, x => x.issueDate, true, false, false, null);

export class ActiveFPassportsInfoV2 {
  @_ p1: ActiveFPassportInfoV2 = new ActiveFPassportInfoV2();
  @_ p2: ActiveFPassportInfoV2 = new ActiveFPassportInfoV2();
}

export class ApplicantV2 {
  @_ gender: Gender = new Gender();
  @_ personIdentity: PersonIdentity = new PersonIdentity();
  @_ nameChanges: FeatureArray<NameChange> = new FeatureArray<NameChange>(NameChange, []);
  @_ lastNameLat: string = '';
  @_ firstNameLat: string = '';
  @_ birthPlaceFull: string = '';
  @_ birthPlaceRus: string = '';
  @_ birthPlaceLat: string = '';
  @_ permAddress: PermAddressV2 = new PermAddressV2();
  @_ tempAddress: TempAddressV2 = new TempAddressV2();
  @_ email: string = '';
  @_ phone: string = '';
  @_ activeFPassport: boolean = true;
  @_ activeFPassports: ActiveFPassportsInfoV2 = new ActiveFPassportsInfoV2();
  @_ jobRecords: FeatureArray<JobRecord> = new FeatureArray<JobRecord>(JobRecord, []);
  @_ jobManDate: MaskedDate = new MaskedDate();
  @_ jobManName: string = '';
  @_ hrManager: HRManager = new HRManager();
  @_ identityDocument: ApplicantIdentityDocument = new ApplicantIdentityDocument();
  @_ chipPhoto: ImageBlob = new ImageBlob();
  @_ printPhoto: ImageBlob = new ImageBlob();
  @_ signature: ImageBlob = new ImageBlob();
  @_ fingerprints: FingerprintsBlob = new FingerprintsBlob();
  @_ secondNationality: NullableString = new NullableString();
}

Features.defaultValue(ApplicantV2, x => x.lastNameLat, Features.transliterate(x => x.personIdentity.fullName.lastName));
Features.defaultValue(ApplicantV2, x => x.firstNameLat, Features.transliterate(x => x.personIdentity.fullName.firstName));
Features.maskedDateOptions(ApplicantV2, x => x.jobManDate, true, false, false, null);

export class RepresentativeProofDocumentTypeV2 extends Enum {
  static readonly BirthCertificate: RepresentativeProofDocumentTypeV2 = new RepresentativeProofDocumentTypeV2('1');
  static readonly Trustee: RepresentativeProofDocumentTypeV2 = new RepresentativeProofDocumentTypeV2('6');
}

export class RepresentativeProofDocumentV2 {
  @_ type: RepresentativeProofDocumentTypeV2 = new RepresentativeProofDocumentTypeV2();
  @_ series: string = '';
  @_ number: string = '';
  @_ issuer: string = '';
  @_ issueDate: MaskedDate = new MaskedDate();
}
Features.stringOptions(RepresentativeProofDocumentV2, x => x.issuer, true, false, false);
Features.maskedDateOptions(RepresentativeProofDocumentV2, x => x.issueDate, true, false, false, null);

export class IndependentChildDocumentTypeV2 extends Enum {
  static readonly SelfSufficientCourtDecision: IndependentChildDocumentTypeV2 = new IndependentChildDocumentTypeV2('7');
  static readonly SelfSufficientMarriageCertificate: IndependentChildDocumentTypeV2 = new IndependentChildDocumentTypeV2('8');
  static readonly SelfSufficientGuardianDecision: IndependentChildDocumentTypeV2 = new IndependentChildDocumentTypeV2('9');
}

export class IndependentChildDocumentV2 {
  @_ type: IndependentChildDocumentTypeV2 = new IndependentChildDocumentTypeV2();
  @_ series: string = '';
  @_ number: string = '';
  @_ issuer: string = '';
  @_ issueDate: MaskedDate = new MaskedDate();
}

Features.stringOptions(IndependentChildDocumentV2, (x) => x.issuer, true, false, false);
Features.maskedDateOptions(IndependentChildDocumentV2, x => x.issueDate, true, false, false, null);

export class TrustRecipientV2 {
  @_ active: boolean = false;
  @_ gender: Gender = new Gender();
  @_ personIdentity: PersonIdentity = new PersonIdentity();
  @_ birthPlace: string = '';
}

export class RepresentativeV2 {
  @_ gender: Gender = new Gender();
  @_ personIdentity: PersonIdentity = new PersonIdentity();
  @_ birthPlace: string = '';
  @_ permAddress: PermAddressV2 = new PermAddressV2();
  @_ tempAddress: TempAddressV2 = new TempAddressV2();
  @_ email: string = '';
  @_ phone: string = '';
  @_ identityDocument: RepresentativeIdentityDocument = new RepresentativeIdentityDocument();
  @_ representativeProof: RepresentativeProofDocumentV2 = new RepresentativeProofDocumentV2();
  @_ trustRecipient: TrustRecipientV2 = new TrustRecipientV2();
}

export class RequestCauseV2 extends Enum {
  static readonly FirstTime: RequestCauseV2 = new RequestCauseV2('0');
  static readonly AdditionToActive: RequestCauseV2 = new RequestCauseV2('8');
  static readonly InsteadOfActive: RequestCauseV2 = new RequestCauseV2('7');
  static readonly InsteadOfLostPassport: RequestCauseV2 = new RequestCauseV2('3');
  static readonly InsteadOfUsedOrSpoiltPassport: RequestCauseV2 = new RequestCauseV2('6');
}

export class RestrictionV2 extends Enum {
  static readonly NotRestricted: RestrictionV2 = new RestrictionV2('0');
  static readonly Restricted: RestrictionV2 = new RestrictionV2('1');
}

export class ExtendedRestrictionV2 {
  @_ value: RestrictionV2 = new RestrictionV2();
  @_ organization: string = '';
  @_ year: MaskedDate = new MaskedDate();
}
Features.maskedDateOptions(ExtendedRestrictionV2, x => x.year, false, false, true, null);

export class AccreditedOrganizationV2 {
  @_ code: string = '';
  @_ name: string = '';
}

export class RequestFeaturesV2 {
  @_ applicant: ApplicantV2 = new ApplicantV2();
  @_ representative: RepresentativeV2 = new RepresentativeV2();
  @_ incapable: boolean = false;
  @_ independentChild: boolean = false;
  @_ independentChildDocument: IndependentChildDocumentV2 = new IndependentChildDocumentV2();
  @_ desiredForeignPassportType: ForeignPassportType = ForeignPassportType.Common;
  @_ personalizedForeignPassport: PersonalizedFPassportInfo = new PersonalizedFPassportInfo();
  @_ cause: RequestCauseV2 = new RequestCauseV2();
  @_ secretAccess: ExtendedRestrictionV2 = new ExtendedRestrictionV2();
  @_ contractObligations: ExtendedRestrictionV2 = new ExtendedRestrictionV2();
  @_ militaryService: RestrictionV2 = new RestrictionV2();
  @_ convicted: RestrictionV2 = new RestrictionV2();
  @_ accused: RestrictionV2 = new RestrictionV2();
  @_ legalObligations: RestrictionV2 = new RestrictionV2();
  @_ bankrupt: RestrictionV2 = new RestrictionV2();
  @_ mailing: FPMailingInfo = new FPMailingInfo();
  @_ notificationOfRefusalFormFields: NotificationOfRefusalFormFields = new NotificationOfRefusalFormFields();
  @_ citizenshipCertificate: CitizenshipCertificate = new CitizenshipCertificate();
  @_ adultSpecialConditions: AdultSpecialConditions = AdultSpecialConditions.None;
  @_ teenagerSpecialConditions: TeenagerSpecialConditions = TeenagerSpecialConditions.None;
  @_ presidentEdict: PresidentEdict = new PresidentEdict();
  @_ citizenshipDecision: CitizenshipDecision = new CitizenshipDecision();
  @_ soldierPermission: SoldierPermission = new SoldierPermission();
  @_ dipServiceControlData: DipServiceControlData = new DipServiceControlData();
  @_ fingerprintsRequired: FingerprintsRequired = new FingerprintsRequired();
  @_ fingerprintsPresent: boolean = false;
  @_ receptionUser: string = '';
  @_ receptionUserPosition: string = '';
  @_ receptionDate: MaskedDate = new MaskedDate();
  @_ applicantDate: MaskedDate = new MaskedDate();
  @_ inputMedia: InputMedia = new InputMedia();
  @_ mobileOptions: MobileOptions = new MobileOptions();
  @_ ambulanceOptions: AmbulanceOptions = new AmbulanceOptions();
  @_ stationaryOptions: StationaryOptions = new StationaryOptions();
  @_ versionSite: string = '';
  @_ versionAmbulance: string = '';
  @_ instanceAmbulance: string = '';
  @_ versionClient: string = '';
  @_ versionServer: string = '';
  @_ inputMode: InputMode = new InputMode();
  @_ inputWorkPlace: string = '';
  @_ priority: RequestPriority = RequestPriority.None;
  @_ soldier: boolean = false;
  @_ recentCitizenship: boolean = false;
  @_ accreditedOrganization: AccreditedOrganizationV2 = new AccreditedOrganizationV2();
  @_ sourceRequestUID: string = '';
  @_ cloneUIDs: string[] = [];
  @_ goal: RequestGoal = new RequestGoal();

  get justification(): string | null {
    if (this.presidentEdict?.number) {
      return uFormat(
        FeaturesI18N.getString("field.justification.presidentEdict"),
        this.presidentEdict.number,
        Utils.dateToDotsFormat(UTCDate.iso(this.presidentEdict.date.value)),
      );
    } else if (this.soldierPermission?.number) {
      return uFormat(
        FeaturesI18N.getString("field.justification.soldierPermission"),
        this.soldierPermission.number,
        Utils.dateToDotsFormat(UTCDate.iso(this.soldierPermission.date.value)),
        this.soldierPermission.signerPost,
        this.soldierPermission.signerName,
        this.soldierPermission.agreementNumber,
        Utils.dateToDotsFormat(UTCDate.iso(this.soldierPermission.agreementDate.value)),
        this.soldierPermission.deciderName,
        this.soldierPermission.militaryUnit
      );
    } else if (this.citizenshipDecision?.number) {
      return uFormat(
        FeaturesI18N.getString("field.justification.citizenshipDecision"),
        this.citizenshipDecision.number,
        Utils.dateToDotsFormat(UTCDate.iso(this.citizenshipDecision.date.value)),
        this.citizenshipDecision.officerPost,
        this.citizenshipDecision.officerName,
        this.citizenshipDecision.article
      );
    } else {
      return null;
    }
  }

  get showCitizenshipCertificate(): boolean {
    return (
      this.inputMode === InputMode.RkzuLCOD ||
      this.inputMode === InputMode.RkzuUT ||
      this.inputMode === InputMode.VO ||
      this.inputMode === InputMode.HSM
    );
  }

  get citizenshipCertificateString(): string | null {
    if (this.citizenshipCertificate.number !== '') {
      return uFormat(
        "№ {0} от {1} {2} {3}",
        this.citizenshipCertificate.number,
        this.citizenshipCertificate.date.value,
        this.citizenshipCertificate.officerPost,
        this.citizenshipCertificate.officerName
      );
    } else {
      return null;
    }
  }

  isAdult(): boolean {
    return Features.ageBetween(this.receptionDate, false, this.applicant.personIdentity.birthDate, false, 18, null, true);
  }

  isTeenager(): boolean {
    return Features.ageBetween(this.receptionDate, false, this.applicant.personIdentity.birthDate, false, 14, 18, false);
  }

  isFingerprintCapable(): boolean {
    return Features.ageBetween(this.receptionDate, false, this.applicant.personIdentity.birthDate, false, 12, null, true);
  }

  isDesiredPassportTypeDipService(): boolean {
    return this.desiredForeignPassportType === ForeignPassportType.Service || this.desiredForeignPassportType === ForeignPassportType.Diplomatic;
  }

  canDepartmentIssueDiplomaticPassport(): boolean {
    // коды подразделений КД, АП и УК
    return (this.stationaryOptions &&
      (this.stationaryOptions.departmentLong === '200007701' ||
        this.stationaryOptions.departmentLong === '200007794' ||
        this.stationaryOptions.departmentLong === '200007795'));
  }

  isHomelandDepartment(): boolean {
    // режимы АП (ФСО), УК, ПСФ, КД
    return (this.stationaryOptions &&
      (this.stationaryOptions.departmentLong === '200007794' ||
        this.stationaryOptions.departmentLong === '200007795' ||
        this.stationaryOptions.departmentLong === '200003901' ||
        this.stationaryOptions.departmentLong === '200007701'));
  }

  applicantIdentityDocTypesList(): ApplicantIdentityDocumentType[] {
    if (this.isDesiredPassportTypeDipService()) {
      // Для ДиС ограничений на на типы документов нет
      return [
        ApplicantIdentityDocumentType.NoDocument,
        ApplicantIdentityDocumentType.RussianPassport,
        ApplicantIdentityDocumentType.BirthCertificate
      ];
    }
    if (this.isHomelandDepartment()) {
      // Для подразделений на территории РФ
      if (this.isAdult() || this.isTeenager()) {
        return [ApplicantIdentityDocumentType.RussianPassport];
      } else {
        return [ApplicantIdentityDocumentType.BirthCertificate];
      }
    }
    else {
      // Для РКЗУ
      if (this.isAdult() || this.isTeenager()) {
        return [ApplicantIdentityDocumentType.NoDocument, ApplicantIdentityDocumentType.RussianPassport];
      } else {
        return [ApplicantIdentityDocumentType.NoDocument, ApplicantIdentityDocumentType.BirthCertificate];
      }
    }
  }

  applicantIdentityDocTypesDefault(): ApplicantIdentityDocumentType | null {
    const isIdentityDocumentFilled = !!this.applicant.identityDocument.issueDate?.value || !!this.applicant.identityDocument.issuer || !!this.applicant.identityDocument.number || !!this.applicant.identityDocument.series;
    // Если документ начали заполнять, при смене даты рождения тип документа по умолчанию обновляться не будет
    if (isIdentityDocumentFilled) {
      return null;
    }
    if (this.isDesiredPassportTypeDipService()) {
      return ApplicantIdentityDocumentType.NoDocument;
    }
    if (this.isAdult() || this.isTeenager()) {
      return ApplicantIdentityDocumentType.RussianPassport;
    }
    return ApplicantIdentityDocumentType.BirthCertificate;
  }

  representativeProofTypesList(): RepresentativeProofDocumentTypeV2[] {
    return [RepresentativeProofDocumentTypeV2.BirthCertificate, RepresentativeProofDocumentTypeV2.Trustee];
  }

  representativeProofTypesDefault(): RepresentativeProofDocumentTypeV2 {
    return this.isAdult() ? RepresentativeProofDocumentTypeV2.Trustee : RepresentativeProofDocumentTypeV2.BirthCertificate;
  }

  goalTypesList(): RequestGoal[] {
    return [RequestGoal.TemporaryTrips];
  }

  goalTypesDefault(): RequestGoal {
    return RequestGoal.TemporaryTrips;
  }

  adultSpecialConditionsList(): AdultSpecialConditions[] {
    if (this.stationaryOptions && this.stationaryOptions.departmentLong === '200003901') {
      return [AdultSpecialConditions.None, AdultSpecialConditions.SoldierPermission];
    }
    return [AdultSpecialConditions.None, AdultSpecialConditions.CitizenshipDecision, AdultSpecialConditions.PresidentEdict, AdultSpecialConditions.SoldierPermission];
  }

  adultSpecialConditionsDefault(): AdultSpecialConditions | null {
    return null;
  }

  desiredForeignPassportTypesList(): ForeignPassportType[] {
    if (this.canDepartmentIssueDiplomaticPassport()) {
      return [ForeignPassportType.Common, ForeignPassportType.Service, ForeignPassportType.Diplomatic];
    }
    return [ForeignPassportType.Common];
  }

  desiredForeignPassportTypesDefault(): ForeignPassportType | null {
    return null;
  }

  teenagerSpecialConditionsList(): TeenagerSpecialConditions[] {
    if (this.stationaryOptions && this.stationaryOptions.departmentLong === '200003901') {
      return [TeenagerSpecialConditions.None];
    }
    return [TeenagerSpecialConditions.None, TeenagerSpecialConditions.CitizenshipDecision, TeenagerSpecialConditions.PresidentEdict];
  }

  teenagerSpecialConditionsDefault(): TeenagerSpecialConditions | null {
    return null;
  }

  asString(): string {
    return this.applicant.personIdentity.fullName.asString();
  }

  get asString_(): string {
    return this.asString();
  }

  asShortString(): string {
    return this.applicant.personIdentity.fullName.asShortString();
  }

  get FPassportDisplayInfo(): string | undefined {
    const passportDisplayString: (p: ActiveFPassportInfoV2 | undefined) => string = p => {
      if (!p || !p.series && !p.number) {
        return '';
      }
      const lostAndCanceledSubString = `${p.mustBeCanceled ? 'А' : ''}${p.lost && p.mustBeCanceled ? ';' : ''}${p.lost ? 'У' : ''}`;
      return `${p.series}${p.number}${lostAndCanceledSubString ? `(${lostAndCanceledSubString})` : ''}`;
    }

    const firstPassport = passportDisplayString(this.applicant?.activeFPassports?.p1);
    if (!firstPassport) {
      return '';
    }

    const secondPassport = passportDisplayString(this.applicant?.activeFPassports?.p2);

    return `${firstPassport}${!!secondPassport ? ', ' : ''}${secondPassport} `;
  }

  copyAppPermAddressToTempAddress() {
    this.applicant.tempAddress.address.country = this.applicant.permAddress.address.country;
    this.applicant.tempAddress.address.admDivision.value = this.applicant.permAddress.address.admDivision.value;
    this.applicant.tempAddress.address.region.value = this.applicant.permAddress.address.region.value;
    this.applicant.tempAddress.address.settlement = this.applicant.permAddress.address.settlement;
    this.applicant.tempAddress.address.street.value = this.applicant.permAddress.address.street.value;
    this.applicant.tempAddress.address.building.value = this.applicant.permAddress.address.building.value;
    this.applicant.tempAddress.address.housing.value = this.applicant.permAddress.address.housing.value;
    this.applicant.tempAddress.address.structure.value = this.applicant.permAddress.address.structure.value;
    this.applicant.tempAddress.address.flat.value = this.applicant.permAddress.address.flat.value;
    this.applicant.tempAddress.from.value = this.applicant.permAddress.from.value;
    this.applicant.tempAddress.to.value = '';
  }

  copyRepPermAddressToTempAddress() {
    this.representative.tempAddress.address.country = this.representative.permAddress.address.country;
    this.representative.tempAddress.address.admDivision.value = this.representative.permAddress.address.admDivision.value;
    this.representative.tempAddress.address.region.value = this.representative.permAddress.address.region.value;
    this.representative.tempAddress.address.settlement = this.representative.permAddress.address.settlement;
    this.representative.tempAddress.address.street.value = this.representative.permAddress.address.street.value;
    this.representative.tempAddress.address.building.value = this.representative.permAddress.address.building.value;
    this.representative.tempAddress.address.housing.value = this.representative.permAddress.address.housing.value;
    this.representative.tempAddress.address.structure.value = this.representative.permAddress.address.structure.value;
    this.representative.tempAddress.address.flat.value = this.representative.permAddress.address.flat.value;
    this.representative.tempAddress.from.value = this.representative.permAddress.from.value;
    this.representative.tempAddress.to.value = '';
  }

  copyAppPermAddressToRepPermAddress() {
    this.representative.permAddress.address.country = this.applicant.permAddress.address.country;
    this.representative.permAddress.address.admDivision.value = this.applicant.permAddress.address.admDivision.value;
    this.representative.permAddress.address.region.value = this.applicant.permAddress.address.region.value;
    this.representative.permAddress.address.settlement = this.applicant.permAddress.address.settlement;
    this.representative.permAddress.address.street.value = this.applicant.permAddress.address.street.value;
    this.representative.permAddress.address.building.value = this.applicant.permAddress.address.building.value;
    this.representative.permAddress.address.housing.value = this.applicant.permAddress.address.housing.value;
    this.representative.permAddress.address.structure.value = this.applicant.permAddress.address.structure.value;
    this.representative.permAddress.address.flat.value = this.applicant.permAddress.address.flat.value;
    this.representative.permAddress.from.value = this.applicant.permAddress.from.value;
  }

  copyAppTempAddressToRepTempAddress() {
    this.representative.tempAddress.address.country = this.applicant.tempAddress.address.country;
    this.representative.tempAddress.address.admDivision.value = this.applicant.tempAddress.address.admDivision.value;
    this.representative.tempAddress.address.region.value = this.applicant.tempAddress.address.region.value;
    this.representative.tempAddress.address.settlement = this.applicant.tempAddress.address.settlement;
    this.representative.tempAddress.address.street.value = this.applicant.tempAddress.address.street.value;
    this.representative.tempAddress.address.building.value = this.applicant.tempAddress.address.building.value;
    this.representative.tempAddress.address.housing.value = this.applicant.tempAddress.address.housing.value;
    this.representative.tempAddress.address.structure.value = this.applicant.tempAddress.address.structure.value;
    this.representative.tempAddress.address.flat.value = this.applicant.tempAddress.address.flat.value;
    this.representative.tempAddress.from.value = this.applicant.tempAddress.from.value;
    this.representative.tempAddress.to.value = this.applicant.tempAddress.to.value;
    this.representative.tempAddress.type = this.applicant.tempAddress.type;
  }

  copyAppPermAddressToRepTempAddress() {
    this.representative.tempAddress.address.country = this.applicant.permAddress.address.country;
    this.representative.tempAddress.address.admDivision.value = this.applicant.permAddress.address.admDivision.value;
    this.representative.tempAddress.address.region.value = this.applicant.permAddress.address.region.value;
    this.representative.tempAddress.address.settlement = this.applicant.permAddress.address.settlement;
    this.representative.tempAddress.address.street.value = this.applicant.permAddress.address.street.value;
    this.representative.tempAddress.address.building.value = this.applicant.permAddress.address.building.value;
    this.representative.tempAddress.address.housing.value = this.applicant.permAddress.address.housing.value;
    this.representative.tempAddress.address.structure.value = this.applicant.permAddress.address.structure.value;
    this.representative.tempAddress.address.flat.value = this.applicant.permAddress.address.flat.value;
    this.representative.tempAddress.from.value = this.applicant.permAddress.from.value;
  }

  swapDocumentsFlags(path: string) {
    if (this.citizenshipCertificate
      && path === "applicant.activeFPassports.p1.next"
      && this.applicant?.activeFPassports?.p1?.next) {
      this.citizenshipCertificate.active = false;
    }
    if (
      this.applicant.activeFPassports.p1
      && path === "citizenshipCertificate.active"
      && this.citizenshipCertificate?.active) {
      this.applicant.activeFPassports.p1.next = false;
    }
  }

  switchOffHrManagerMiddleNameFlag(path: string) {
    if (path === "desiredForeignPassportType"
      && this.applicant?.hrManager?.fullName?.middleName) {
      this.applicant.hrManager.fullName.middleName.value = null;
    }
  }

  switchOffDipServiceDataFlags(path: string) {
    if (path === "desiredForeignPassportType"
      && this.dipServiceControlData?.secretAware) {
      this.dipServiceControlData.secretAware.value = null;
    };
    if (path === "desiredForeignPassportType"
      && this.dipServiceControlData?.secretAdmitted) {
      this.dipServiceControlData.secretAdmitted.value = null;
    };
    if (path === "desiredForeignPassportType"
      && this.dipServiceControlData?.contracts) {
      this.dipServiceControlData.contracts.value = null;
    };
    if (path === "desiredForeignPassportType"
      && this.dipServiceControlData?.trips) {
      this.dipServiceControlData.trips.value = null;
    };
  }

  switchOffSecondNationalityFlag(path: string) {
    if (path === "desiredForeignPassportType"
      && this.applicant?.secondNationality) {
      this.applicant.secondNationality.value = null;
    };
  }

  switchOffAddressFlags(path: string) {
    if (path === "desiredForeignPassportType"
      && this.applicant.permAddress.address.admDivision.value === '') {
      this.applicant.permAddress.address.admDivision.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.permAddress.address.region.value === '') {
      this.applicant.permAddress.address.region.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.permAddress.address.street.value === '') {
      this.applicant.permAddress.address.street.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.permAddress.address.building.value === '') {
      this.applicant.permAddress.address.building.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.permAddress.address.housing.value === '') {
      this.applicant.permAddress.address.housing.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.permAddress.address.structure.value === '') {
      this.applicant.permAddress.address.structure.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.permAddress.address.flat.value === '') {
      this.applicant.permAddress.address.flat.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.permAddress.address.admDivision.value === '') {
      this.applicant.permAddress.address.admDivision.value = null;
    }

    if (path === "desiredForeignPassportType"
      && this.applicant.tempAddress.address.admDivision.value === '') {
      this.applicant.tempAddress.address.admDivision.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.tempAddress.address.region.value === '') {
      this.applicant.tempAddress.address.region.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.tempAddress.address.street.value === '') {
      this.applicant.tempAddress.address.street.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.tempAddress.address.building.value === '') {
      this.applicant.tempAddress.address.building.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.tempAddress.address.housing.value === '') {
      this.applicant.tempAddress.address.housing.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.tempAddress.address.structure.value === '') {
      this.applicant.tempAddress.address.structure.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.tempAddress.address.flat.value === '') {
      this.applicant.tempAddress.address.flat.value = null;
    }
    if (path === "desiredForeignPassportType"
      && this.applicant.tempAddress.address.admDivision.value === '') {
      this.applicant.tempAddress.address.admDivision.value = null;
    }
  }

  setReceptionOfficerPosition(data: string) {
    this.receptionUserPosition = data;
  }

  formatPermAddress(): string {
    return this.formatAddress(this.applicant.permAddress.address);
  }

  formatTempAddress(): string {
    return this.formatAddress(this.applicant.tempAddress.address);
  }

  formatAddress(address: PersonAddressV2): string {
    let parts: string[] = [];
    if (!!address.country) parts.push(address.country);
    if (!!address.admDivision.value) parts.push(address.admDivision.value);
    if (!!address.region.value) parts.push(address.region.value);
    if (!!address.settlement) parts.push(address.settlement);
    if (!!address.street.value) parts.push('ул. ' + address.street.value);
    if (!!address.building.value) parts.push('д. ' + address.building.value);
    if (!!address.housing.value) parts.push('корп. ' + address.housing.value);
    if (!!address.structure.value) parts.push('стр. ' + address.structure.value);
    if (!!address.flat.value) parts.push('кв. ' + address.flat.value);
    return parts.join(', ').toUpperCase();
  }

  formatUnemployment(): string {
    return this.applicant.gender.val() !== "1"
      ? FeaturesI18N.getString("field.jobRecords.unemployed-male")
      : FeaturesI18N.getString("field.jobRecords.unemployed-female");
  }

  get disableSignature() {
    return !this.isAdult() && !this.isTeenager();
  }

  get disableFingerprints() {
    return !this.isFingerprintCapable();
  }

  onBeforeSave(dashSign: () => void) {
    if (this.disableSignature) {
      dashSign();
      this.applicant.signature.scanned = true;
    }
    if (this.ambulanceOptions) {
      if (this.disableFingerprints) {
        this.applicant.fingerprints = new FingerprintsBlob();
      }
    }
    this.applicant.personIdentity.fullName.firstName = Utils.trimNameField(this.applicant.personIdentity.fullName.firstName);
    this.applicant.personIdentity.fullName.lastName = Utils.trimNameField(this.applicant.personIdentity.fullName.lastName);
    if (this.applicant.personIdentity.fullName.middleName.value) {
      this.applicant.personIdentity.fullName.middleName.value = Utils.trimNameField(this.applicant.personIdentity.fullName.middleName.value);
    }
    for (const x of this.applicant.nameChanges) {
      x.firstName = Utils.trimNameField(x.firstName);
      x.lastName = Utils.trimNameField(x.lastName);
      if (x.middleName.value) {
        x.middleName.value = Utils.trimNameField(x.middleName.value);
      }
    }
    this.applicant.lastNameLat = Utils.trimNameField(this.applicant.lastNameLat);
    this.applicant.firstNameLat = Utils.trimNameField(this.applicant.firstNameLat);
    this.applicant.birthPlaceFull = Utils.trimBirthPlaceField(this.applicant.birthPlaceFull);
    this.applicant.birthPlaceLat = Utils.trimBirthPlaceField(this.applicant.birthPlaceLat);
    this.applicant.birthPlaceRus = Utils.trimBirthPlaceField(this.applicant.birthPlaceRus);

    if (this.representative) {
      this.representative.personIdentity.fullName.firstName = Utils.trimNameField(this.representative.personIdentity.fullName.firstName);
      this.representative.personIdentity.fullName.lastName = Utils.trimNameField(this.representative.personIdentity.fullName.lastName);

      const representativeMiddleName = this.representative.personIdentity.fullName.middleName.value;
      if (representativeMiddleName) {
        this.representative.personIdentity.fullName.middleName.value = Utils.trimNameField(representativeMiddleName);
      };
    }
  }
}

Features.defaultValue(RequestFeaturesV2, x => x.representative.representativeProof.series, Features.copyIf(x => x.applicant.identityDocument.series, x => x.representative.representativeProof.type.val() === x.applicant.identityDocument.type.val()));
Features.defaultValue(RequestFeaturesV2, x => x.representative.representativeProof.number, Features.copyIf(x => x.applicant.identityDocument.number, x => x.representative.representativeProof.type.val() === x.applicant.identityDocument.type.val()));
Features.defaultValue(RequestFeaturesV2, x => x.representative.representativeProof.issuer, Features.copyIf(x => x.applicant.identityDocument.issuer, x => x.representative.representativeProof.type.val() === x.applicant.identityDocument.type.val()));
Features.defaultValue(RequestFeaturesV2, x => x.representative.representativeProof.issueDate, Features.copyIf(x => x.applicant.identityDocument.issueDate, x => x.representative.representativeProof.type.val() === x.applicant.identityDocument.type.val()));

Features.documentField(RequestFeaturesV2, DocumentField.Photo, x => x.applicant.chipPhoto);
Features.documentField(RequestFeaturesV2, DocumentField.PhotoHash, x => x.applicant.chipPhoto.hash);
Features.documentField(RequestFeaturesV2, DocumentField.PassportType, 'P');
Features.documentField(RequestFeaturesV2, DocumentField.PassportSubType, x => x.desiredForeignPassportType);
Features.documentField(RequestFeaturesV2, DocumentField.Country, 'RUS');
Features.documentField(RequestFeaturesV2, DocumentField.LastName, x => x.applicant.lastNameLat);
Features.documentField(RequestFeaturesV2, DocumentField.FirstName, x => x.applicant.firstNameLat);
Features.documentField(RequestFeaturesV2, DocumentField.SerialNumber, x => x.personalizedForeignPassport.number);
Features.documentField(RequestFeaturesV2, DocumentField.Nationality, 'RUS');
Features.documentField(RequestFeaturesV2, DocumentField.BirthDate, x => x.applicant.personIdentity.birthDate);
Features.documentField(RequestFeaturesV2, DocumentField.ExpiryDate, x => x.personalizedForeignPassport.expireDate);
Features.documentField(RequestFeaturesV2, DocumentField.Gender, x => x.applicant.gender);

Features.keyboardLanguage(RequestFeaturesV2, x => x.applicant.email, 'en');
Features.keyboardLanguage(RequestFeaturesV2, x => x.representative.email, 'en');

Features.enumOptions(ApplicantV2, x => x.identityDocument.type, x => x.personIdentity.birthDate, (x: RequestFeaturesV2) => x.applicantIdentityDocTypesList(), (x: RequestFeaturesV2) => x.applicantIdentityDocTypesDefault());
Features.enumOptions(RepresentativeV2, x => x.representativeProof.type, x => x.personIdentity.birthDate, (x: RequestFeaturesV2) => x.representativeProofTypesList(), (x: RequestFeaturesV2) => x.representativeProofTypesDefault());
Features.enumOptions(RequestFeaturesV2, x => x.adultSpecialConditions, x => x.applicant.personIdentity.birthDate, (x: RequestFeaturesV2) => x.adultSpecialConditionsList(), (x: RequestFeaturesV2) => x.adultSpecialConditionsDefault());
Features.enumOptions(RequestFeaturesV2, x => x.teenagerSpecialConditions, x => x.applicant.personIdentity.birthDate, (x: RequestFeaturesV2) => x.teenagerSpecialConditionsList(), (x: RequestFeaturesV2) => x.teenagerSpecialConditionsDefault());
Features.enumOptions(RequestFeaturesV2, x => x.desiredForeignPassportType, x => x.desiredForeignPassportType, (x: RequestFeaturesV2) => x.desiredForeignPassportTypesList(), (x: RequestFeaturesV2) => x.desiredForeignPassportTypesDefault());
Features.enumOptions(RequestFeaturesV2, x => x.goal, x => x.desiredForeignPassportType, (x: RequestFeaturesV2) => x.goalTypesList(), (x: RequestFeaturesV2) => x.goalTypesDefault());
